import type { Country } from "@/types";

import { useEffect, useState } from "react";

import {
  FormError,
  FormModal,
  ImageTile,
  PlainCheckbox,
  PlainTextField,
} from "@/components/kit";
import { CountryRepository } from "@/repositories/country";

const countryRepository = new CountryRepository();

interface CountryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Set to edit that country; leave out to add a new one. */
  country?: Country | null;
  onSuccess: () => void;
}

const EMPTY = { name: "", nameAr: "", image: "", id: "", isVisible: false };

export default function CountryFormModal({
  isOpen,
  onClose,
  country,
  onSuccess,
}: CountryFormModalProps) {
  const isEdit = !!country;
  const [formData, setFormData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (country) {
      setFormData({
        name: country.name,
        nameAr: country.nameAr || "",
        image: country.image,
        id: "",
        isVisible: country.isVisible,
      });
      setError("");
    }
  }, [country]);

  const set = <K extends keyof typeof EMPTY>(
    key: K,
    value: (typeof EMPTY)[K],
  ) => setFormData((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    setLoading(true);
    setError("");

    try {
      if (country) {
        await countryRepository.update(country.id, {
          name: formData.name,
          nameAr: formData.nameAr,
          image: formData.image,
          isVisible: formData.isVisible,
        });
      } else {
        // Only include id in the request if it's provided
        await countryRepository.add({
          name: formData.name,
          nameAr: formData.nameAr,
          image: formData.image,
          isVisible: formData.isVisible,
          ...(formData.id && { id: formData.id }),
        });
        setFormData(EMPTY);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          (isEdit
            ? "Failed to update country. Please try again."
            : "Failed to create country. Please try again."),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError("");
    if (!isEdit) setFormData(EMPTY);
    onClose();
  };

  return (
    <FormModal
      nativeValidation
      isOpen={isOpen}
      isSubmitting={loading}
      submitLabel={isEdit ? "Save" : "Add country"}
      title={isEdit ? "Edit country" : "Add country"}
      onClose={handleClose}
      onSubmit={handleSubmit}
    >
      <FormError message={error} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-4">
        <PlainTextField
          isRequired
          label="Country name"
          name="name"
          placeholder="Enter country name"
          value={formData.name}
          onChange={(value) => set("name", value)}
        />
        <PlainTextField
          isRequired
          dir="rtl"
          label="Country name (Arabic)"
          name="nameAr"
          placeholder="Country name in Arabic"
          value={formData.nameAr}
          onChange={(value) => set("nameAr", value)}
        />
      </div>
      <PlainTextField
        isRequired
        label="Flag image URL"
        name="image"
        placeholder="Enter flag image URL"
        type="url"
        value={formData.image}
        onChange={(value) => set("image", value)}
      />
      {formData.image ? (
        <div className="flex items-center gap-3">
          <ImageTile
            alt={formData.name || "Country flag"}
            size="sm"
            src={formData.image}
          />
          <span className="text-muted text-sm">
            {formData.name || "Flag preview"}
          </span>
        </div>
      ) : null}
      {isEdit ? null : (
        <PlainTextField
          isMono
          description="Leave empty to auto-generate from country name"
          label="Country ID (optional)"
          name="id"
          placeholder="e.g., united-states"
          value={formData.id}
          onChange={(value) => set("id", value)}
        />
      )}
      <PlainCheckbox
        isSelected={formData.isVisible}
        label="Visible"
        onChange={(selected) => set("isVisible", selected)}
      />
    </FormModal>
  );
}
